import { Injectable } from '@angular/core';
import {
  AccessibleOfficeDto,
  ApplicationView,
  SystemRoleBit,
  UserRestService,
} from '@gh/builder-shared-data';
import { ConfigurationHttpRequestInterceptor } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { CONTEXT_PATH } from '@gh/config';
import { AuthService } from '@gh/core-auth';
import { Enum } from '@gh/core-data';
import { DialogService } from '@gh/core-ui';
import { EMPTY$, of$ } from '@gh/rx';
import { catchError$, map$, shareReplay$, switchMap$ } from '@gh/rx/operators';
import { Observable } from 'rxjs';
import { BuilderUserContext } from './builder-user-context';
import {
  SelectOfficeDialogComponent,
  SelectOfficeDialogConfig,
} from './select-office-dialog/select-office-dialog.component';

export const ACTIVE_OFFICE_STORAGE_KEY = 'auth:activeOfficeId';

/**
 * Resolves which office scopes the builder session after Keycloak login,
 * and supports in-app switching between accessible offices.
 */
@Injectable()
export class OfficeAccessService {
  constructor(
    private userRest: UserRestService,
    private dialogService: DialogService,
    private configurationHttpRequestInterceptor: ConfigurationHttpRequestInterceptor,
    private authService: AuthService,
  ) {}

  private cachedAccessibleOffices$?: Observable<AccessibleOfficeDto[]>;

  findAccessibleOffices(): Observable<AccessibleOfficeDto[]> {
    return this.userRest.findAccessibleOffices().pipe(
      catchError$(() => of$([])),
      map$((offices) => offices || []),
    );
  }

  /**
   * Multi-office: true when the signed-in user has the Salesperson role in an office reached through a membership
   * (listed as not their home office), with or without Franchisee: a shared user is never that office's owning
   * Franchisee. Reassign flows are hidden for them; home / normal offices are unaffected. Accessible offices are
   * fetched once per page load.
   */
  isReassignRestricted(context: BuilderUserContext): Observable<boolean> {
    const officeId = context.office?.id;
    const isMembershipSalesperson = context.hasSystemRole(SystemRoleBit.SalesPerson)
      && !context.hasSystemRole(SystemRoleBit.Master)
      && !context.hasSystemRole(SystemRoleBit.Corporate);
    if (!officeId || !isMembershipSalesperson) {
      return of$(false);
    }
    if (!this.cachedAccessibleOffices$) {
      this.cachedAccessibleOffices$ = this.findAccessibleOffices().pipe(shareReplay$(1));
    }
    return this.cachedAccessibleOffices$.pipe(
      map$((offices) => offices.some((office) => String(office.officeId) === String(officeId) && office.homeOffice === false)),
    );
  }

  /**
   * Call before GET /profiles/me when the URL does not already carry office/master context.
   * - Master URL: skip
   * - Office URL: persist that id
   * - Otherwise: GET accessible-offices → auto-select (1) or dialog (2+)
   * - Empty list: skip (corporate / master users with no office memberships)
   */
  resolveOfficeContext(params: Hash<any>): Observable<void> {
    if (params['appView'] === ApplicationView.Master) {
      return of$(void 0);
    }

    if (params['appView'] === ApplicationView.Office && params['appViewId']) {
      this.persistActiveOffice(params['appViewId']);
      return of$(void 0);
    }

    return this.findAccessibleOffices().pipe(
      switchMap$((offices: AccessibleOfficeDto[]) => this.selectOffice(offices, Boolean(params['sandbox']))),
    );
  }

  getStoredActiveOfficeId(): Maybe<string> {
    try {
      return window.sessionStorage.getItem(ACTIVE_OFFICE_STORAGE_KEY) || void 0;
    } catch {
      return void 0;
    }
  }

  clearActiveOffice(): void {
    try {
      window.sessionStorage.removeItem(ACTIVE_OFFICE_STORAGE_KEY);
    } catch {
      void 0;
    }
  }

  applyOfficeContext(officeId: ID): void {
    this.persistActiveOffice(officeId);
    this.configurationHttpRequestInterceptor.setHeader(
      'X-AppView',
      Enum.toValue(ApplicationView, ApplicationView.Office),
    );
    this.configurationHttpRequestInterceptor.setHeader('X-OfficeId', String(officeId));
    try {
      window.sessionStorage.setItem('auth:justLoggedInOffice', '1');
    } catch {
      void 0;
    }
  }

  /**
   * Same-tab hard switch into the selected office context (no cross-office data bleed).
   */
  switchToOffice(officeId: ID, options?: { sandbox?: boolean }): void {
    this.applyOfficeContext(officeId);
    const sandboxPrefix = options?.sandbox ? 'sandbox/' : '';
    window.location.assign(`${CONTEXT_PATH}${sandboxPrefix}office-view/${officeId}/dashboard`);
  }

  private selectOffice(offices: AccessibleOfficeDto[], sandbox: boolean): Observable<void> {
    if (!offices.length) {
      return of$(void 0);
    }

    const storedId = this.getStoredActiveOfficeId();
    const storedOffice = storedId
      ? offices.find((office) => String(office.officeId) === String(storedId))
      : void 0;

    if (storedOffice) {
      this.applyOfficeContext(storedOffice.officeId);
      return of$(void 0);
    }

    if (storedId) {
      this.clearActiveOffice();
    }

    if (offices.length === 1) {
      this.applyOfficeContext(offices[0].officeId);
      return of$(void 0);
    }

    return this.dialogService
      .openWithInput(
        SelectOfficeDialogComponent,
        <SelectOfficeDialogConfig>{ offices },
        { disableClose: true, width: '560px', maxWidth: '95vw', panelClass: 'office-picker-dialog-panel' },
      )
      .afterClosed()
      .pipe(
        switchMap$((selected: Maybe<AccessibleOfficeDto>) => {
          if (!selected?.officeId) {
            // Picker closed without choosing: sign out so the user lands on the login page
            // and can sign in again, e.g. with a different account. Keycloak redirects away,
            // so the login flow is left pending instead of failing with an error dialog.
            this.clearActiveOffice();
            return this.authService.logout().pipe(switchMap$(() => EMPTY$));
          }
          // Reload straight into the chosen office (same as Switch Office) instead of continuing the bootstrap
          // in this page: the picker opens mid-bootstrap, and finishing the bootstrap at "/" after it closed
          // could leave the first navigation not run (blank page). The reload carries the office in the URL,
          // so the next bootstrap takes the office-view path without opening the picker again.
          this.switchToOffice(selected.officeId, { sandbox });
          return EMPTY$;
        }),
      );
  }

  private persistActiveOffice(officeId: ID): void {
    try {
      window.sessionStorage.setItem(ACTIVE_OFFICE_STORAGE_KEY, String(officeId));
    } catch {
      void 0;
    }
  }
}
